<template>
  <div
    ref="autocomplete"
    class="autocomplete"
    @blur="handleBlur"
  >
    <input
      :id="inputId"
      ref="inputValue"
      v-model="inputValue"
      autocomplete="off"
      @input="$emit('getSuggestions', inputValue)"
      @keyup.enter.prevent="$emit('submitInput', inputValue)"
      @keydown.down="navigateSuggestions"
      @keydown.up="navigateSuggestions"
      @blur="handleBlur"
      @focus="showSuggestions = suggestions.length > 0"
    >
    <div
      v-if="showSuggestions"
      class="suggestions"
    >
      <div
        v-for="(suggestion, index) in suggestions"
        ref="suggestions"
        :key="index"
        tabindex="0"
        class="suggestion"
        @keyup.enter.prevent="selectSuggestion(suggestion)"
        @click.prevent="selectSuggestion(suggestion)"
        @keydown.down="navigateSuggestions"
        @keydown.up="navigateSuggestions"
        @blur="handleBlur"
      >
        {{ suggestion }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    initialValue: {
      type: String,
      default: ''
    },
    inputId: {
      type: String,
      default: 'inputValue'
    },
    suggestions: {
      type: Array,
      default: () => []
    }
  },
  emits: [
    'getSuggestions',
    'setInput',
    'submitInput'
  ],
  data() {
    return {
      inputValue: '',
      showSuggestions: false,
      selectedIndex: -1,
    }
  },
  watch: {
    suggestions() {
      this.selectedIndex = -1
      this.showSuggestions = this.suggestions.length > 0
    },
    initialValue(newValue) {
      // there's an odd situation where this component gets instantiated before
      // the parent component has computed what the initial value should be.
      if (newValue) {
        this.$refs.inputValue.value = this.initialValue
      }
    }
  },
  mounted() {
    if (this.initialValue) {
      this.$refs.inputValue.value = this.initialValue
    }
    window.addEventListener('popstate', (event) => {
      this.inputValue = new URLSearchParams(window.location.search).get('query')
    })
  },
  methods: {
    selectSuggestion(suggestion) {
      this.inputValue = suggestion
      this.selectedIndex = -1
      this.$refs.inputValue.focus()
      this.showSuggestions = false
      this.$emit('setInput', this.inputValue)
    },
    navigateSuggestions(e) {
      // handles navigating through the suggestions
      e.preventDefault() // don't scroll on arrow keys
      if (e.code === 'ArrowUp') {
        this.selectedIndex = Math.max(-1, this.selectedIndex - 1)
      } else if (e.code === 'ArrowDown') {
        this.selectedIndex = Math.min(this.suggestions.length - 1, this.selectedIndex + 1)
      }
      if (this.selectedIndex === -1) {
        this.$refs.inputValue.focus()
      } else {
        this.$refs.suggestions.at(this.selectedIndex).focus()
      }
    },
    handleBlur(e) {
      // handles the closing of the suggestions when user clicks off component
      let blurRelatedTarget = e.relatedTarget
      if (blurRelatedTarget) {
        this.showSuggestions = (
          this.$refs.suggestions?.includes(blurRelatedTarget) ||
          blurRelatedTarget === this.$refs.inputValue ||
          blurRelatedTarget === this.$refs.autocomplete
        )
      } else {
        // the blur related target can be null which just means the user
        // clicked somewhere in the document that had no element
        this.showSuggestions = false
      }

      if (!this.showSuggestions) {
        this.selectedIndex = -1
      }
    }
  }
}
</script>

<style scoped>
.autocomplete {
  position: relative;

  .suggestions {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 10;
    transform: translateY(2.49em);
    background: #fff;
    border: 2px solid #cecbca;
    width: 100%;
    padding: 0.4em 0.3em;

    .suggestion {
      cursor: pointer;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      padding: 0.1em 0.3em;

      &:focus, &:hover, &:active {
        outline: none;
        background: #b3d4fc;
      }
    }
  }
}
</style>
